/**
 * TeamTab — people and AI teammates in one chat (kiaan-team-v1.html §2).
 *
 * Left: channels + direct chats. Middle: messages, polled every 5 s. Right:
 * who is in the channel + tasks made from this chat. In a direct chat with an
 * agent a message comes back with `taskIssueId` and shows as a task chip.
 *
 * Agents in channels (SUN-71, mockup kiaan-channels-agents.html): agents are members
 * with an "agent" badge and a status dot; `@` picks from this channel's members with a
 * one-line role; an agent's reply shows as its own message (task card when it names an
 * issue); Kiaan's own lines (chain limit, stop) sit centred. Admins (kiaan_manage_agents,
 * also checked on the server) add/remove members and get "Stop agents here".
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthStore } from '../../stores/auth';
import { KiaanMarkdown } from './KiaanMarkdown';
import { AttachButton, MessageFiles, PendingFiles, useChatUploads } from './ChatAttachments';
import {
  KIAAN_UNREACHABLE,
  kiaanErrorText,
  kiaanServerText,
  listAgents,
  listChannels,
  listMessages,
  listPeople,
  createChannel,
  postMessage,
  uploadChannelFiles,
  getChannelAgents,
  addChannelMember,
  removeChannelMember,
  stopChannelAgents,
  type ChannelAgentState,
  type KiaanAgent,
  type KiaanChannel,
  type KiaanMessage,
  type KiaanPerson,
} from './kiaanApi';

export const TEAM_POLL_MS = 5000;
export const CHAIN_LIMIT = 6;
export const chainText = (n: number, limit = CHAIN_LIMIT) => `Agents talked ${n} times in a row · limit ${limit} before a person must reply`;

interface Mentionable {
  key: string;
  name: string;
  kind: 'person' | 'agent';
  /** One line under the name in the @ picker. */
  role?: string;
  status?: string | null;
}

/** Status dot: working = blue, ready = green, paused/off/unknown = grey (mockup). */
function dotClass(status: string | null | undefined): string {
  if (status === 'working') return 'kw-dot kw-dot-b';
  if (status === 'ready') return 'kw-dot kw-dot-g';
  return 'kw-dot kw-dot-o';
}

/** Agent messages in a row at the end (Kiaan's own lines skipped, a person ends the count). */
export function trailingAgents(messages: KiaanMessage[]): number {
  let n = 0;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const t = messages[i].authorType;
    if (t === 'system') continue;
    if (t !== 'agent') break;
    n += 1;
  }
  return n;
}

/** `@Name` of a channel member shown as a tag chip; everything else as plain text. */
function withTags(body: string, names: string[]): React.ReactNode {
  const sorted = [...new Set(names)].filter(Boolean).sort((a, b) => b.length - a.length);
  if (sorted.length === 0) return body;
  const esc = sorted.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`(@(?:${esc.join('|')}))(?![A-Za-z0-9_])`, 'gi');
  const parts = body.split(re);
  return parts.map((part, i) => (i % 2 === 1 ? <span key={i} className="kw-tag">{part}</span> : part));
}

const AV_COLOURS = ['#7a4a8c', '#3A6A8C', '#6b8a3a', '#8c5a3a', '#4a7a8c'];
function avColour(id: string): string {
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return AV_COLOURS[h % AV_COLOURS.length];
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // IST always (house rule), not the browser's or the server's clock zone.
  return d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' }).replace(/\s?(am|pm)/i, '');
}

export interface TeamTabProps {
  /** May call agents, add/remove members, stop agents (kiaan_manage_agents). The server checks too. */
  canCallAgents?: boolean;
}

export function TeamTab({ canCallAgents = false }: TeamTabProps = {}) {
  const me = useAuthStore((s: { user?: { id: string } | null }) => s.user?.id ?? null);
  const [channels, setChannels] = useState<KiaanChannel[] | null>(null);
  const [down, setDown] = useState<string | null>(null);
  const [active, setActive] = useState<string | null>(null);
  const [messages, setMessages] = useState<KiaanMessage[]>([]);
  const [agents, setAgents] = useState<KiaanAgent[]>([]);
  const [people, setPeople] = useState<KiaanPerson[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [mentionIdx, setMentionIdx] = useState(0);
  const [making, setMaking] = useState<null | 'channel' | 'direct'>(null);
  const [newName, setNewName] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [makeError, setMakeError] = useState<string | null>(null);
  const [agentState, setAgentState] = useState<ChannelAgentState | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [stopping, setStopping] = useState(false);
  const endRef = useRef<HTMLDivElement | null>(null);
  const lastIdRef = useRef<string | undefined>(undefined);
  const uploadHere = useCallback((files: File[]) => uploadChannelFiles(active as string, files), [active]);
  const uploads = useChatUploads(active ? uploadHere : null);
  const { clear: clearUploads } = uploads;
  useEffect(() => { clearUploads(); }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  // Channels, agents, people — once.
  useEffect(() => {
    let alive = true;
    listChannels()
      .then((list) => {
        if (!alive) return;
        setChannels(list);
        setDown(null);
        if (list.length > 0) setActive((a) => a ?? list[0].id);
      })
      .catch((err) => { if (alive) { setChannels([]); setDown(kiaanErrorText(err)); } });
    listAgents().then((a) => { if (alive) setAgents(a); }).catch(() => {});
    listPeople().then((p) => { if (alive) setPeople(p); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Messages for the active channel — load, then poll every 5 s with ?after=.
  const load = useCallback(async (channelId: string, after?: string) => {
    const list = await listMessages(channelId, after);
    if (list.length === 0) return;
    setMessages((prev) => {
      const seen = new Set(prev.map((m) => m.id));
      const fresh = list.filter((m) => !seen.has(m.id));
      const next = after ? [...prev, ...fresh] : list;
      lastIdRef.current = next[next.length - 1]?.id;
      return next;
    });
  }, []);

  useEffect(() => {
    if (!active) return;
    let alive = true;
    setMessages([]);
    lastIdRef.current = undefined;
    load(active).catch(() => {});
    const t = window.setInterval(() => {
      if (!alive) return;
      load(active, lastIdRef.current).catch(() => {});
    }, TEAM_POLL_MS);
    return () => { alive = false; window.clearInterval(t); };
  }, [active, load]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [messages.length]);

  const channel = useMemo(() => channels?.find((c) => c.id === active) ?? null, [channels, active]);
  const hasAgents = !!channel && channel.kind === 'channel' && (channel.members ?? []).some((m) => m.memberType === 'agent');

  // Agents' status dots, roles, "is writing…" — polled with the messages, only where agents are members.
  useEffect(() => {
    setAgentState(null);
    setNotice(null);
    if (!active || !hasAgents) return;
    let alive = true;
    const pull = () => getChannelAgents(active).then((s) => { if (alive) setAgentState(s); }).catch(() => {});
    pull();
    const t = window.setInterval(pull, TEAM_POLL_MS);
    return () => { alive = false; window.clearInterval(t); };
  }, [active, hasAgents]);

  const agentInfo = useMemo(() => new Map((agentState?.agents ?? []).map((a) => [a.id, a])), [agentState]);
  const groupChannels = useMemo(() => (channels ?? []).filter((c) => c.kind === 'channel'), [channels]);
  const directs = useMemo(() => (channels ?? []).filter((c) => c.kind === 'direct'), [channels]);
  const tasks = useMemo(() => messages.filter((m) => m.taskIssueId), [messages]);

  const mentionables = useMemo<Mentionable[]>(() => {
    const out: Mentionable[] = [];
    const seen = new Set<string>();
    const agentRole = (id: string) => agentInfo.get(id)?.role || agents.find((a) => a.id === id)?.role || 'agent';
    for (const m of channel?.members ?? []) {
      const key = `${m.memberType}:${m.memberId}`;
      if (seen.has(key) || (m.memberType === 'user' && m.memberId === me)) continue;
      seen.add(key);
      out.push(m.memberType === 'agent'
        ? { key, name: m.name, kind: 'agent', role: agentRole(m.memberId), status: agentInfo.get(m.memberId)?.status ?? null }
        : { key, name: m.name, kind: 'person', role: 'person' });
    }
    // A group channel's @ picker lists its members only (SUN-71); a direct chat may reach anyone.
    if (channel?.kind === 'channel') return out;
    for (const p of people) {
      const key = `user:${p.id}`;
      if (!seen.has(key)) { seen.add(key); out.push({ key, name: p.name, kind: 'person', role: 'person' }); }
    }
    for (const a of agents) {
      const key = `agent:${a.id}`;
      if (!seen.has(key)) { seen.add(key); out.push({ key, name: a.name, kind: 'agent', role: a.role }); }
    }
    return out;
  }, [channel, people, agents, agentInfo, me]);

  // "@" autocomplete: text after the last "@" that has no space yet.
  const mentionQuery = useMemo(() => {
    const at = draft.lastIndexOf('@');
    if (at < 0) return null;
    const q = draft.slice(at + 1);
    if (/\s/.test(q)) return null;
    return q.toLowerCase();
  }, [draft]);
  const suggestions = useMemo(() => {
    if (mentionQuery === null) return [];
    return mentionables.filter((m) => m.name.toLowerCase().startsWith(mentionQuery)).slice(0, 6);
  }, [mentionQuery, mentionables]);
  useEffect(() => { setMentionIdx(0); }, [mentionQuery]);

  const pick = (m: Mentionable) => {
    const at = draft.lastIndexOf('@');
    setDraft(`${draft.slice(0, at)}@${m.name} `);
  };

  const send = async () => {
    const body = draft.trim();
    if ((!body && uploads.pending.length === 0) || !active || sending || uploads.busy) return;
    setSending(true);
    setSendError(null);
    setNotice(null);
    try {
      const { woken, notice: why, ...msg } = await postMessage(active, body, ...(uploads.pending.length ? [uploads.pending.map((a) => a.id)] : []));
      setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
      lastIdRef.current = msg.id;
      setDraft('');
      clearUploads();
      const lines = [woken && woken.length ? `Woke ${woken.join(', ')}.` : '', why ?? ''].filter(Boolean);
      setNotice(lines.length ? lines.join(' ') : null);
    } catch (err) {
      // A refused file or a bad send has its own plain reason; anything else is "not reachable".
      const status = (err as { response?: { status?: number } } | null)?.response?.status;
      setSendError(status === 400 ? kiaanServerText(err) : KIAAN_UNREACHABLE);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (suggestions.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx((i) => (i + 1) % suggestions.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIdx((i) => (i - 1 + suggestions.length) % suggestions.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pick(suggestions[mentionIdx]); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); }
  };

  const channelItem = (c: KiaanChannel) => (
    <button key={c.id} type="button" className={`kw-it${active === c.id ? ' kw-on' : ''}`} onClick={() => setActive(c.id)} data-testid="kiaan-channel">
      {c.kind === 'channel' ? (
        <span># {c.name}</span>
      ) : (
        <>
          <span className="kw-av" style={{ background: avColour(c.id) }}>{c.name.charAt(0).toUpperCase()}</span>
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</span>
        </>
      )}
      {c.unread ? <span className="kw-n">{c.unread}</span> : null}
    </button>
  );

  const startMaking = (kind: 'channel' | 'direct') => { setMaking(kind); setNewName(''); setPicked([]); setMakeError(null); };
  const everyone = [...people.map((p) => ({ id: p.id, name: p.name, ai: false })), ...agents.map((a) => ({ id: a.id, name: a.name, ai: true }))];
  const togglePick = (id: string) => setPicked((list) => (making === 'direct' ? [id] : list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));
  const submitNew = async () => {
    if (!making) return;
    const name = making === 'direct' ? (everyone.find((e) => e.id === picked[0])?.name ?? '') : newName.trim().replace(/^#/, '');
    if (!name || picked.length === 0) { setMakeError(making === 'direct' ? 'Pick one person or AI.' : 'Give a name and pick at least one member.'); return; }
    try {
      const c = await createChannel({ name, kind: making, memberIds: picked });
      setChannels((list) => [...(list ?? []), c]);
      setActive(c.id);
      setMaking(null);
    } catch (err) {
      setMakeError(kiaanErrorText(err));
    }
  };

  const replaceChannel = (c: KiaanChannel) => setChannels((list) => (list ?? []).map((x) => (x.id === c.id ? { ...x, ...c } : x)));
  const addMember = async (value: string) => {
    if (!active || !value) return;
    const [type, id] = value.split(':') as ['user' | 'agent', string];
    try {
      replaceChannel(await addChannelMember(active, type, id));
      setAdding(false);
    } catch (err) {
      setNotice(kiaanErrorText(err));
    }
  };
  const removeMember = async (type: 'user' | 'agent', id: string) => {
    if (!active) return;
    try {
      replaceChannel(await removeChannelMember(active, type, id));
    } catch (err) {
      setNotice(kiaanErrorText(err));
    }
  };
  const stopAgents = async () => {
    if (!active || stopping) return;
    setStopping(true);
    try {
      const { message } = await stopChannelAgents(active);
      setMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message]));
      lastIdRef.current = message.id;
      setAgentState((s) => (s ? { ...s, writing: [] } : s));
    } catch (err) {
      setNotice(kiaanErrorText(err));
    } finally {
      setStopping(false);
    }
  };

  const memberNames = (channel?.members ?? []).map((m) => m.name);
  const peopleCount = (channel?.members ?? []).filter((m) => m.memberType === 'user').length;
  const agentCount = (channel?.members ?? []).filter((m) => m.memberType === 'agent').length;
  const chain = trailingAgents(messages);
  const lastIsSystem = messages[messages.length - 1]?.authorType === 'system';
  const writingNames = (agentState?.writing ?? [])
    .map((id) => (channel?.members ?? []).find((m) => m.memberType === 'agent' && m.memberId === id)?.name)
    .filter(Boolean) as string[];
  const notMembers = channel?.kind === 'channel'
    ? [
        ...people.filter((p) => !(channel.members ?? []).some((m) => m.memberType === 'user' && m.memberId === p.id)).map((p) => ({ value: `user:${p.id}`, label: p.name })),
        ...agents.filter((a) => !(channel.members ?? []).some((m) => m.memberType === 'agent' && m.memberId === a.id)).map((a) => ({ value: `agent:${a.id}`, label: `${a.name} (agent)` })),
      ]
    : [];

  return (
    <>
      {down && <div className="kw-banner" role="status">{down}</div>}
      <div className="kw-cols">
        <aside className="kw-side" aria-label="Channels">
          <div className="kw-sec" style={{ marginTop: 0 }}>Channels</div>
          {groupChannels.map(channelItem)}
          {channels && groupChannels.length === 0 && <div className="kw-small">No channels yet.</div>}
          <div className="kw-sec">Direct</div>
          {directs.map(channelItem)}
          {channels && directs.length === 0 && <div className="kw-small">No direct chats yet.</div>}
          {!making && (
            <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
              <button type="button" className="kw-btn kw-light" onClick={() => startMaking('channel')}>＋ New channel</button>
              <button type="button" className="kw-btn kw-light" onClick={() => startMaking('direct')}>＋ Direct chat</button>
            </div>
          )}
          {making && (
            <div className="kw-card" style={{ marginTop: 10 }} aria-label={making === 'channel' ? 'New channel' : 'New direct chat'}>
              {making === 'channel' && (
                <input className="kw-input" placeholder="Channel name, e.g. sales-north" value={newName} onChange={(e) => setNewName(e.target.value)} aria-label="Channel name" />
              )}
              <div className="kw-small" style={{ margin: '6px 0 4px' }}>{making === 'channel' ? 'Members' : 'Chat with'}</div>
              <div style={{ maxHeight: 160, overflowY: 'auto' }}>
                {everyone.map((e) => (
                  <label key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, padding: '2px 0' }}>
                    <input type={making === 'direct' ? 'radio' : 'checkbox'} name="kiaan-members" checked={picked.includes(e.id)} onChange={() => togglePick(e.id)} />
                    {e.name}{e.ai ? ' (AI)' : ''}
                  </label>
                ))}
              </div>
              {makeError && <div className="kw-small" role="alert" style={{ color: 'var(--danger)' }}>{makeError}</div>}
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <button type="button" className="kw-btn" onClick={submitNew}>Create</button>
                <button type="button" className="kw-btn kw-light" onClick={() => setMaking(null)}>Cancel</button>
              </div>
            </div>
          )}
        </aside>

        <section className="kw-main" aria-label="Messages" {...uploads.dropProps}>
          {channel?.kind === 'channel' && (
            <div className="kw-chhead">
              <b># {channel.name}</b>
              <span className="kw-small">{peopleCount} {peopleCount === 1 ? 'person' : 'people'}, {agentCount} {agentCount === 1 ? 'agent' : 'agents'}</span>
              {canCallAgents && hasAgents && (
                <button type="button" className="kw-stop" onClick={() => void stopAgents()} disabled={stopping}>■ Stop agents here</button>
              )}
            </div>
          )}
          <div className="kw-msgs">
            {channel && messages.length === 0 && <div className="kw-small">No messages yet. Say hello.</div>}
            {messages.map((m) => {
              if (m.authorType === 'system') {
                return <div key={m.id} className="kw-sys" data-testid="kiaan-message">{m.body}</div>;
              }
              const mine = m.authorType === 'user' && me !== null && m.authorId === me;
              const isAgent = m.authorType === 'agent';
              const name = m.authorName ?? (isAgent ? 'AI' : 'Someone');
              return (
                <div key={m.id} data-testid="kiaan-message" className={isAgent ? 'kw-agentmsg' : undefined}>
                  {!mine && (
                    <div className="kw-who">
                      {isAgent ? <>{name} <span className="kw-badge-agent">agent</span> · {fmtTime(m.createdAt)}</> : `${name} · ${fmtTime(m.createdAt)}`}
                    </div>
                  )}
                  {(m.body || !m.attachments?.length) && (
                    <div className={`kw-m ${mine ? 'kw-me' : isAgent ? 'kw-bot' : 'kw-person'}`}>{isAgent ? <KiaanMarkdown text={m.body} /> : withTags(m.body, memberNames)}</div>
                  )}
                  <MessageFiles files={m.attachments} />
                  {isAgent && m.taskIdentifier && (
                    <div className="kw-task" data-testid="kiaan-task-card">
                      📋 Task {m.taskIdentifier} · {m.taskTitle}{m.taskTeam ? ` · ${m.taskTeam}` : ''}
                    </div>
                  )}
                  {!m.taskIdentifier && m.taskIssueId && (
                    <div className="kw-card" style={{ marginTop: 6 }} data-testid="kiaan-task-chip">
                      ✓ Task made: &quot;{m.taskTitle ?? m.body}&quot;{m.taskTeam ? ` → ${m.taskTeam}` : ''}{' '}
                      <span className="kw-pill kw-wip">in Kiaan Work</span>
                    </div>
                  )}
                </div>
              );
            })}
            {chain >= 2 && !lastIsSystem && (
              <div className="kw-sys" data-testid="kiaan-chain">{chainText(chain, agentState?.limit ?? CHAIN_LIMIT)}</div>
            )}
            {writingNames.map((n) => <div key={n} className="kw-typing">{n} is writing…</div>)}
            <div ref={endRef} />
          </div>
          {notice && <div className="kw-small kw-notice" role="status" data-testid="kiaan-notice">{notice}</div>}
          {sendError && <div className="kw-banner kw-danger" role="alert" style={{ margin: '0 12px 8px' }}>{sendError}</div>}
          <PendingFiles uploads={uploads} />
          <div className="kw-in">
            <AttachButton uploads={uploads} disabled={!active || sending} />
            {suggestions.length > 0 && (
              <div className="kw-mention" role="listbox" aria-label="Mention">
                {suggestions.map((s, i) => (
                  <button key={s.key} type="button" role="option" aria-selected={i === mentionIdx} className={i === mentionIdx ? 'kw-on' : ''} onMouseDown={(e) => { e.preventDefault(); pick(s); }}>
                    {s.kind === 'agent' ? (
                      <span className={dotClass(s.status)} />
                    ) : (
                      <span className="kw-av" style={{ background: avColour(s.key) }}>{s.name.charAt(0).toUpperCase()}</span>
                    )}
                    {s.name}
                    {s.kind === 'agent' && <span className="kw-badge-agent">agent</span>}
                    {s.role && <small className="kw-role">{s.role}</small>}
                  </button>
                ))}
              </div>
            )}
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKey}
              onPaste={uploads.onPaste}
              disabled={!active || sending}
              placeholder={channel ? `Message ${channel.kind === 'channel' ? '#' : ''}${channel.name} · type @ to tag a member` : 'Pick a channel'}
              aria-label="Message"
            />
            <button type="button" className="kw-icon-btn" onClick={() => void send()} disabled={(!draft.trim() && uploads.pending.length === 0) || sending || uploads.busy} aria-label="Send">➤</button>
          </div>
        </section>

        <aside className="kw-right" aria-label="In this channel">
          <div className="kw-sec" style={{ marginTop: 0 }}>In this channel</div>
          {(channel?.members ?? []).map((m) => (
            <div key={`${m.memberType}:${m.memberId}`} className="kw-it" style={{ cursor: 'default' }} data-testid="kiaan-member">
              {m.memberType === 'agent' ? (
                <span className={dotClass(agentInfo.get(m.memberId)?.status)} title={agentInfo.get(m.memberId)?.status ?? 'status unknown'} />
              ) : (
                <span className="kw-av" style={{ background: avColour(m.memberId) }}>{m.name.charAt(0).toUpperCase()}</span>
              )}
              <span style={{ flex: 1, minWidth: 0 }}>{m.name}</span>
              {m.memberType === 'agent' && <span className="kw-badge-agent">agent</span>}
              {canCallAgents && channel?.kind === 'channel' && !(m.memberType === 'user' && m.memberId === me) && (
                <button type="button" className="kw-x" aria-label={`Remove ${m.name}`} onClick={() => void removeMember(m.memberType, m.memberId)}>×</button>
              )}
            </div>
          ))}
          {channel && (channel.members ?? []).length === 0 && <div className="kw-small">No members listed.</div>}
          {canCallAgents && channel?.kind === 'channel' && (
            adding ? (
              <select className="kw-input" aria-label="Add member or agent" defaultValue="" onChange={(e) => void addMember(e.target.value)} onBlur={() => setAdding(false)} autoFocus>
                <option value="" disabled>Pick a person or agent…</option>
                {notMembers.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : (
              <button type="button" className="kw-btn kw-light" style={{ marginTop: 6 }} onClick={() => setAdding(true)}>＋ Add member or agent</button>
            )
          )}
          <div className="kw-sec">Tasks from this chat</div>
          {tasks.length === 0 ? (
            <div className="kw-small">No tasks yet.</div>
          ) : (
            tasks.map((t) => (
              <div key={t.id} className="kw-fold">
                {t.taskTitle ?? t.body}
                <span className="kw-pill kw-wip kw-ml">doing</span>
              </div>
            ))
          )}
        </aside>
      </div>
    </>
  );
}
